import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../services/api';

interface Category {
  id: string;
  name: string;
  parentId?: string;
  type: string;
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState({ name: '', type: 'expense' as string, parentId: '' });

  const load = () => api.get<Category[]>('/api/categories').then(setCategories);

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', type: 'expense', parentId: '' });
    setDialogOpen(true);
  };

  const openEdit = (cat: Category) => {
    setEditing(cat);
    setForm({ name: cat.name, type: cat.type, parentId: cat.parentId || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const body = { name: form.name, type: form.type, parentId: form.parentId || undefined };
    if (editing) {
      await api.put(`/api/categories/${editing.id}`, body);
    } else {
      await api.post('/api/categories', body);
    }
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (id: string) => {
    if (confirm('Poistetaanko tämä kategoria?')) {
      await api.delete(`/api/categories/${id}`);
      load();
    }
  };

  // Group categories by parent
  const topLevel = categories.filter((c) => !c.parentId);
  const getChildren = (parentId: string) => categories.filter((c) => c.parentId === parentId);

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          Kategoriat
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
          Lisää
        </Button>
      </Box>

      {topLevel.map((cat) => (
        <Card key={cat.id} sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Box display="flex" alignItems="center">
              <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                {cat.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                {cat.type}
              </Typography>
              <IconButton size="small" onClick={() => openEdit(cat)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(cat.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {getChildren(cat.id).length > 0 && (
              <List dense disablePadding sx={{ pl: 2 }}>
                {getChildren(cat.id).map((child) => (
                  <ListItem key={child.id} disableGutters>
                    <ListItemText primary={child.name} />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => openEdit(child)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(child.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Muokkaa kategoriaa' : 'Uusi kategoria'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nimi"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Tyyppi</InputLabel>
              <Select
                value={form.type}
                label="Tyyppi"
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <MenuItem value="expense">Meno</MenuItem>
                <MenuItem value="income">Tulo</MenuItem>
                <MenuItem value="both">Molemmat</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Yläkategoria</InputLabel>
              <Select
                value={form.parentId}
                label="Yläkategoria"
                onChange={(e) => setForm({ ...form, parentId: e.target.value })}
              >
                <MenuItem value="">Ei yläkategoriaa</MenuItem>
                {topLevel.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Peruuta</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
